'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { match } from 'ts-pattern';
import { loadTgsCanvas } from '~/components/animated-sticker/tgs-canvas';
import { useLatestValueRef } from '~/lib/use-latest-value-ref';
import { isPngFile, isWebMFile } from '../../lib/animated-stickers';
import { Skeleton } from '../skeleton';

function getStickerType(flags: { isPng: boolean; isWebM: boolean }): 'canvas' | 'image' | 'video' {
  if (flags.isPng) return 'image';
  if (flags.isWebM) return 'video';
  return 'canvas';
}

interface AnimatedStickerProps {
  src: string;
  className?: string;
  loop?: boolean;
  autoplay?: boolean;
  leading?: boolean;
  noFullWidth?: boolean;
  onFinished?: () => void;
}

export function AnimatedSticker({
  src,
  className = '',
  loop = true,
  autoplay = true,
  leading,
  noFullWidth,
  onFinished,
}: AnimatedStickerProps) {
  const t = useTranslations('ui.error');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isWebM, setIsWebM] = useState(false);
  const [isPng, setIsPng] = useState(false);
  const [pngObjectUrl, setPngObjectUrl] = useState<string | null>(null);
  const [webmObjectUrl, setWebmObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  const latestRef = useLatestValueRef({ onFinished });

  useEffect(() => {
    const subscriptions: { unsubscribe: () => void }[] = [];
    let currentObjectUrl: string | null = null;

    let hasUnmounted = false;
    subscriptions.push({
      unsubscribe: () => {
        hasUnmounted = true;
      },
    });

    const loadAnimation = async () => {
      try {
        setLoading(true);
        setError(null);
        setIsWebM(false);
        setIsPng(false);
        setPngObjectUrl(null);
        setWebmObjectUrl(null);
        setIsVideoLoaded(false);

        const response = await fetch(src);
        if (!response.ok) {
          throw new Error(`Failed to fetch animation file: ${response.status}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);

        if (hasUnmounted) {
          return;
        }

        if (isPngFile(uint8Array)) {
          const blob = new Blob([arrayBuffer], { type: 'image/png' });
          currentObjectUrl = URL.createObjectURL(blob);
          setPngObjectUrl(currentObjectUrl);
          setIsPng(true);
          setLoading(false);
        } else if (isWebMFile(uint8Array)) {
          const blob = new Blob([arrayBuffer], { type: 'video/webm' });
          currentObjectUrl = URL.createObjectURL(blob);
          setWebmObjectUrl(currentObjectUrl);
          setIsWebM(true);
          setLoading(false);
        } else {
          await loadTgsCanvas({
            uint8Array,
            autoplay,
            loop,
            hasUnmounted: () => hasUnmounted,
            canvasRef,
            onLoaded: () => {
              setLoading(false);
            },
            onComplete: () => {
              latestRef.current.onFinished?.();
            },
          });
        }
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Failed to load animation');
        setLoading(false);
      }
    };

    void loadAnimation();

    return () => {
      subscriptions.forEach((sub) => {
        sub.unsubscribe();
      });
      if (currentObjectUrl) {
        URL.revokeObjectURL(currentObjectUrl);
      }
    };
  }, [src, loop, autoplay, latestRef]);

  const width = match({ noFullWidth, leading })
    .with({ noFullWidth: true }, () => undefined)
    .with({ leading: true }, () => '80%')
    .otherwise(() => '100%');

  const height = leading ? '80%' : '100%';

  if (error) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-800 rounded-lg ${className}`}
        style={{
          width,
          height,
          aspectRatio: '1/1',
        }}
      >
        <div className="text-gray-400 text-sm text-center p-2">{t('failed-to-load-animation')}</div>
      </div>
    );
  }

  function getStyle() {
    return {
      width,
      height,
      marginLeft: leading ? 'auto' : '0',
      marginRight: leading ? 'auto' : '0',
      aspectRatio: '1/1',
      objectFit: 'cover' as const,
    };
  }

  return (
    <div
      className={className}
      style={{
        borderRadius: 16,
        overflow: 'hidden',
        width,
        height,
        marginLeft: leading ? 'auto' : '0',
        marginRight: leading ? 'auto' : '0',
        aspectRatio: '1/1',
        position: 'relative',
      }}
    >
      {loading || (isWebM && !isVideoLoaded) ? (
        <Skeleton
          className="absolute inset-0 flex items-center justify-center bg-gray-800 rounded-lg"
          style={{
            width: '100%',
            height: '100%',
            aspectRatio: '1/1',
          }}
        />
      ) : null}
      {match(getStickerType({ isPng, isWebM }))
        .with('canvas', () => <canvas ref={canvasRef} style={getStyle()} />)
        .with('image', () => (pngObjectUrl ? <img alt="" src={pngObjectUrl} style={getStyle()} /> : null))
        .with('video', () => (
          <video
            autoPlay={autoplay}
            loop={loop}
            muted
            playsInline
            preload="auto"
            src={webmObjectUrl ?? src}
            style={{
              ...getStyle(),
              opacity: isVideoLoaded ? 1 : 0,
            }}
            onError={() => {
              setError(t('failed-to-load-sticker'));
            }}
            onLoadedData={() => {
              setIsVideoLoaded(true);
            }}
            onLoadedMetadata={(event) => {
              // iOS Safari keeps the video canvas black with autoplay=false
              // until a seek forces frame decode. Nudging currentTime renders
              // the first frame as a poster.
              if (event.currentTarget.currentTime === 0) {
                event.currentTarget.currentTime = 0.001;
              }
            }}
          />
        ))
        .exhaustive()}
    </div>
  );
}
